import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { languageAtom, receptionButtonTypeAtom, receptionCompanyIdAtom } from "~/Atom";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import "~/css/reception/MenuReceptionPage.css";
import "~/css/reception/DeliveryCompaniesList.css";
import "~/css/reception/common.css";
import * as icon from "~/Icons";
import {
  getActivationData,
  getAppSettingsData,
  getPhoneBooksEntity,
  type ActivationData,
  type AppSettingsNormalized,
  type PhoneBooksEntity,
} from "~/utils/receptionDB";
import {
  NotificationType,
  PlanType,
  ScreenType,
  type MenuWords,
  type SettingMenuButtonEntity,
} from "~/utils/receptionDB.types";
import {
  getMenuWordsByLanguage,
  useInactivityTimeout,
  mapReceptionLanguage,
} from "~/utils/receptionUtils";
import { ROUTE } from "~/constants/routes";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import { buildErrorPath, mapErrorCode } from "~/utils/error";
import {
  buildDeliveryReceptionErrorCode,
  resolveCallDestinationAction,
  resolveDeliveryFlowType,
  executeDeliveryChatPost,
  postDeliveryReceptionIgnoreError,
} from "~/utils/deliveryFlowUtils";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";
import ReceptionHeader from "~/components/ReceptionHeader";

// SettingMenuButtonEntity["buttonOrder"]からboolean型のキー（表示フラグ）を自動導出
type ButtonOrder = SettingMenuButtonEntity["buttonOrder"];
type DisplayFlag = NonNullable<
  {
    [K in keyof ButtonOrder]: NonNullable<ButtonOrder[K]> extends boolean ? K : never;
  }[keyof ButtonOrder]
>;

// SettingMenuButtonEntity["buttonOrder"]からnumber型のキー（表示順インデックス）を自動導出
type OrderKey = NonNullable<
  {
    [K in keyof ButtonOrder]: NonNullable<ButtonOrder[K]> extends number ? K : never;
  }[keyof ButtonOrder]
>;

// MenuWordsのstring型キーを自動導出（ボタンタイトル取得用）
type MenuWordsTitleKey = NonNullable<
  {
    [K in keyof MenuWords]: MenuWords[K] extends string ? K : never;
  }[keyof MenuWords]
>;

type MenuButtonIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type ButtonConfig = {
  Icon: MenuButtonIconComponent;
  route?: string;
  menuWordsKey: MenuWordsTitleKey;
};

const BUTTON_CONFIG_MAP: Record<string, ButtonConfig> = {
  Group: {
    Icon: icon.icon_department,
    route: ROUTE.RECEPTION.GROUP_LIST,
    menuWordsKey: "groupButtonTitle",
  },
  User: {
    Icon: icon.icon_employee,
    route: ROUTE.RECEPTION.SEARCH_USER,
    menuWordsKey: "userButtonTitle",
  },
  Recruit: {
    Icon: icon.icon_recruit,
    menuWordsKey: "recruitButtonTitle",
  },
  Delivery: {
    Icon: icon.icon_delivery,
    route: ROUTE.RECEPTION.DELIVERY_MAP,
    menuWordsKey: "deliveryButtonTitle",
  },
  Other: {
    Icon: icon.icon_other,
    route: ROUTE.RECEPTION.OTHER,
    menuWordsKey: "otherButtonTitle",
  },
  Secretary: {
    Icon: icon.icon_secretary,
    menuWordsKey: "secretaryButtonTitle",
  },
  Taxi: {
    Icon: icon.icon_taxi,
    menuWordsKey: "taxiButtonTitle",
  },
  Qr: {
    Icon: icon.icon_qr,
    route: ROUTE.RECEPTION.QR,
    menuWordsKey: "qrCodeButtonTitle",
  },
  ReceptionCode: {
    Icon: icon.icon_notification_code,
    route: ROUTE.RECEPTION.INPUT_CODE,
    menuWordsKey: "receptionNumberButtonTitle",
  },
  FaceRecognition: {
    Icon: icon.icon_face_recognition,
    route: ROUTE.RECEPTION.FACE_RECOGNITION,
    menuWordsKey: "faceRecognitionButtonTitle",
  },
  ConversationReception: {
    Icon: icon.icon_conversation_reception,
    route: ROUTE.RECEPTION.CONVERSATION.VISITOR,
    menuWordsKey: "conversationReceptionButtonTitle",
  },
};

const BUTTON_ORDER_ENTRIES: { type: string; displayFlag: DisplayFlag; orderKey: OrderKey }[] = [
  { type: "Group", displayFlag: "isDisplayGroupButton", orderKey: "groupButtonIndex" },
  { type: "User", displayFlag: "isDisplayUserButton", orderKey: "userButtonIndex" },
  { type: "Recruit", displayFlag: "isDisplayRecruitButton", orderKey: "recruitButtonIndex" },
  { type: "Delivery", displayFlag: "isDisplayDeliveryButton", orderKey: "deliveryButtonIndex" },
  { type: "Other", displayFlag: "isDisplayOtherButton", orderKey: "otherButtonIndex" },
  { type: "Secretary", displayFlag: "isDisplaySecretaryButton", orderKey: "secretaryButtonIndex" },
  { type: "Taxi", displayFlag: "isDisplayTaxiButton", orderKey: "taxiButtonIndex" },
  { type: "Qr", displayFlag: "isDisplayQrButton", orderKey: "qrButtonIndex" },
  {
    type: "ReceptionCode",
    displayFlag: "isDisplayReceptionCodeButton",
    orderKey: "receptionCodeButtonIndex",
  },
  {
    type: "FaceRecognition",
    displayFlag: "isDisplayFaceRecognitionButton",
    orderKey: "faceRecognitionButtonIndex",
  },
  {
    type: "ConversationReception",
    displayFlag: "isDisplayConversationReceptionButton",
    orderKey: "conversationReceptionButtonIndex",
  },
];

const NO_CALL_DESTINATION = "Q2009";
const FACETIME_PHONEBOOK_CONFLICT = "Q2010";
const DEFAULT_GENERAL_RECEPTION_ERROR = "Q8999";
const INVALID_CHAT_STATUS = "Q8997";
const GROUP_BUTTON_TYPE = "group";
const USER_BUTTON_TYPE = "user";
const DELIVERY_BUTTON_TYPE = "delivery";
const RECRUIT_BUTTON_TYPE = "recruit";
const OTHER_BUTTON_TYPE = "other";
const SECRETARY_BUTTON_TYPE = "secretary";
const TAXI_BUTTON_TYPE = "taxi";
const QR_BUTTON_TYPE = "qr";
const RECEPTION_CODE_BUTTON_TYPE = "receptionCode";
const FACE_RECOGNITION_BUTTON_TYPE = "faceRecognition";
const CONVERSATION_BUTTON_TYPE = "conversation";

const BUTTON_TYPE_BY_MENU_KEY: Partial<Record<MenuWordsTitleKey, string>> = {
  groupButtonTitle: GROUP_BUTTON_TYPE,
  userButtonTitle: USER_BUTTON_TYPE,
  recruitButtonTitle: RECRUIT_BUTTON_TYPE,
  deliveryButtonTitle: DELIVERY_BUTTON_TYPE,
  otherButtonTitle: OTHER_BUTTON_TYPE,
  secretaryButtonTitle: SECRETARY_BUTTON_TYPE,
  taxiButtonTitle: TAXI_BUTTON_TYPE,
  qrCodeButtonTitle: QR_BUTTON_TYPE,
  receptionNumberButtonTitle: RECEPTION_CODE_BUTTON_TYPE,
  faceRecognitionButtonTitle: FACE_RECOGNITION_BUTTON_TYPE,
  conversationReceptionButtonTitle: CONVERSATION_BUTTON_TYPE,
};

type ChatRequestType = "success" | "failed" | "noRequest";

type GeneralReceptionHandlers = {
  onCall: (chatRequestType: ChatRequestType) => void;
  navigateToComplete: () => void;
  navigateToError: (err: unknown) => void;
  navigateToErrorCode: (code: string) => void;
  navigateToInvalidChatStatus: () => void;
};

const buildGeneralReceptionErrorCode = (error: unknown): string => {
  const rawCode = (error as { response?: { data?: { errorCode?: number | string } } })?.response
    ?.data?.errorCode;

  if (typeof rawCode === "number") {
    return mapErrorCode(ScreenType.MENU_RECEPTION, rawCode);
  }

  if (typeof rawCode === "string") {
    const trimmed = rawCode.trim();
    if (!trimmed) {
      return DEFAULT_GENERAL_RECEPTION_ERROR;
    }

    const numericCode = Number(trimmed);
    if (!Number.isNaN(numericCode)) {
      return mapErrorCode(ScreenType.MENU_RECEPTION, numericCode);
    }

    return trimmed;
  }

  return DEFAULT_GENERAL_RECEPTION_ERROR;
};

const toChatRequestType = (isChatSuccess: boolean): ChatRequestType => {
  return isChatSuccess ? "success" : "failed";
};

const isCallFlow = (plan: string, notificationType: string): boolean => {
  return (
    plan === PlanType.CALL ||
    (plan === PlanType.CHAT_CALL && notificationType === NotificationType.Call)
  );
};

const isChatReceptionFlow = (plan: string, notificationType: string): boolean => {
  return (
    (plan === PlanType.CHAT || plan === PlanType.CHAT_CALL) &&
    (notificationType === NotificationType.Chat ||
      notificationType === NotificationType.ChatAndCall)
  );
};

const handleGeneralReceptionSuccess = (
  notificationType: string,
  isChatSuccess: boolean | undefined,
  handlers: GeneralReceptionHandlers
): void => {
  if (notificationType === NotificationType.Chat) {
    if (isChatSuccess === false) {
      handlers.navigateToErrorCode(DEFAULT_GENERAL_RECEPTION_ERROR);
      return;
    }

    handlers.navigateToComplete();
    return;
  }

  if (notificationType === NotificationType.ChatAndCall) {
    if (typeof isChatSuccess !== "boolean") {
      handlers.navigateToInvalidChatStatus();
      return;
    }

    handlers.onCall(toChatRequestType(isChatSuccess));
    return;
  }

  handlers.navigateToErrorCode(DEFAULT_GENERAL_RECEPTION_ERROR);
};

const handleGeneralReceptionFailure = (
  notificationType: string,
  error: unknown,
  handlers: GeneralReceptionHandlers
): void => {
  if (notificationType === NotificationType.Chat) {
    handlers.navigateToError(error);
    return;
  }

  if (notificationType === NotificationType.ChatAndCall) {
    handlers.onCall("failed");
    return;
  }

  handlers.navigateToErrorCode(buildGeneralReceptionErrorCode(error));
};

// menuWordsKeyから訪問者情報ページへのリダイレクト設定をマッピング
const VISITOR_INFO_REDIRECT_MAP: Partial<
  Record<MenuWordsTitleKey, { flagKey: keyof SettingMenuButtonEntity; buttonType: string }>
> = {
  groupButtonTitle: { flagKey: "isDisplayVisitorInfoGroup", buttonType: "group" },
  userButtonTitle: { flagKey: "isDisplayVisitorInfoUser", buttonType: "user" },
  recruitButtonTitle: { flagKey: "isDisplayVisitorInfoRecruit", buttonType: "recruit" },
  otherButtonTitle: { flagKey: "isDisplayVisitorInfoOther", buttonType: "other" },
  secretaryButtonTitle: { flagKey: "isDisplayVisitorInfoSecretary", buttonType: "secretary" },
};

// ボタン数に応じた上段・下段の分割数を算出
const getLayoutCounts = (total: number): { topCount: number; bottomCount: number } => {
  if (total <= 2) return { topCount: total, bottomCount: 0 };
  if (total === 3) return { topCount: 2, bottomCount: 1 };
  if (total === 4) return { topCount: 3, bottomCount: 1 };
  return { topCount: 3, bottomCount: total - 3 };
};

// 現在の言語に対応するmenuWordsを取得するヘルパー関数
const getMenuWords = (
  dbData: AppSettingsNormalized | undefined,
  targetGroupId: number,
  language: string
): MenuWords | null => {
  return getMenuWordsByLanguage(dbData?.menuWords, targetGroupId, language) ?? null;
};

const normalizeGeneralReceptionGroupId = (
  targetGroupDisplayId: number,
  phoneBooksEntity: PhoneBooksEntity | undefined
): number | undefined => {
  if (targetGroupDisplayId === 0) return undefined;
  return phoneBooksEntity?.groupList.find((g) => g.displayId === targetGroupDisplayId)?.groupId;
};

// buttonOrderデータをもとに表示するボタン一覧を生成
const getVisibleButtons = (menuButton: SettingMenuButtonEntity | null): ButtonConfig[] => {
  if (!menuButton) {
    return [];
  }
  const buttonOrder = menuButton.buttonOrder;
  return BUTTON_ORDER_ENTRIES.filter((e) => buttonOrder[e.displayFlag])
    .sort((a, b) => {
      const aIdx = buttonOrder[a.orderKey] ?? Infinity;
      const bIdx = buttonOrder[b.orderKey] ?? Infinity;
      return aIdx - bIdx;
    })
    .map((e) => BUTTON_CONFIG_MAP[e.type]);
};

type MenuButtonCardProps = {
  btn: ButtonConfig;
  isTop: boolean;
  topCount: number;
  bottomCount: number;
  label: string;
  onClick: () => void;
};

// ボタンカードの1枚分レンダリングコンポーネント
const MenuButtonCard: React.FC<MenuButtonCardProps> = ({
  btn,
  isTop,
  topCount,
  bottomCount,
  label,
  onClick,
}) => {
  const Icon = btn.Icon;
  const span = isTop ? Math.floor(6 / topCount) : Math.floor(6 / bottomCount);
  const heightClass = isTop ? "menu-reception-card--top" : "menu-reception-card--bottom";
  const contentClass = isTop
    ? "menu-reception-card-content--column"
    : "menu-reception-card-content--row";
  return (
    <button
      type="button"
      className={`menu-reception-card reception-button--large ${heightClass}`}
      style={{ gridColumn: `span ${span}` }}
      onClick={onClick}
    >
      <div className={`menu-reception-card-content ${contentClass}`}>
        <Icon className="menu-reception-card-icon-image" aria-hidden="true" focusable="false" />
        <span className="menu-reception-card-label typo-h3">{label}</span>
      </div>
    </button>
  );
};

/**
 * 受付メニューのトップ画面を表示するコンポーネント。
 *
 * 主に DB から受付設定を取得して表示内容を初期化し、
 * 無操作タイマーによる画面遷移制御と、受付案内の読み上げを行う。
 */
const MenuReceptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [language] = useAtom(languageAtom);
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);
  const [, setReceptionButtonType] = useAtom(receptionButtonTypeAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });

  const [appointmentExists, setAppointmentExists] = useState(false);
  const [dbData, setDbData] = useState<AppSettingsNormalized | undefined>(undefined);
  const [activationData, setActivationData] = useState<ActivationData | undefined>(undefined);
  const [phoneBooksEntity, setPhoneBooksEntity] = useState<PhoneBooksEntity | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const navigateToMenuReceptionError = useCallback(
    (code: string = DEFAULT_GENERAL_RECEPTION_ERROR) => {
      navigate(buildErrorPath(code));
    },
    [navigate]
  );

  const targetGroupDisplayId = useMemo(() => {
    return receptionCompanyId !== undefined ? Number(receptionCompanyId) : 0;
  }, [receptionCompanyId]);
  const generalReceptionGroupId = normalizeGeneralReceptionGroupId(
    targetGroupDisplayId,
    phoneBooksEntity
  );

  // IndexedDBから非同期でappSettingsを取得
  useEffect(() => {
    void getAppSettingsData()
      .then((data) => {
        if (data) setDbData(data);
      })
      .catch((error) => {
        console.error("アプリ設定の読み込みに失敗しました:", error);
      });
  }, []);

  // IndexedDBからアクティベーションデータを取得する
  useEffect(() => {
    void getActivationData()
      .then((data) => {
        if (data) setActivationData(data);
      })
      .catch((error) => {
        console.error("アクティベーションデータの読み込みに失敗しました:", error);
      });
  }, []);

  // IndexedDBから電話帳エンティティを取得する
  useEffect(() => {
    void getPhoneBooksEntity()
      .then((data) => {
        if (data) setPhoneBooksEntity(data);
      })
      .catch((error) => {
        console.error("電話帳エンティティの読み込みに失敗しました:", error);
      });
  }, []);

  // companyIdに対応するappSetting全体を取得
  const menuButton = useMemo(
    () => dbData?.menuButtons.find((m) => m.id === targetGroupDisplayId) ?? null,
    [dbData, targetGroupDisplayId]
  );

  // 現在の言語に対応するmenuWordsを取得
  const menuWords = useMemo(
    () => getMenuWords(dbData, targetGroupDisplayId, language),
    [dbData, targetGroupDisplayId, language]
  );

  // 配達フロー用: deliveryButtonTitleを取得する
  const deliveryButtonName = useMemo((): string => {
    const langCode = mapReceptionLanguage(language);
    const menuWordEntity = dbData?.menuWords.find((w) => w.id === targetGroupDisplayId);
    return (
      menuWordEntity?.items.find((i) => i.languageCode === langCode)?.menuWords
        .deliveryButtonTitle ?? ""
    );
  }, [dbData, language, targetGroupDisplayId]);

  // 配達フロー用: groupIdを取得する
  const deliveryGroupId = useMemo((): number | undefined => {
    if (targetGroupDisplayId === 0) return undefined;
    return phoneBooksEntity?.groupList.find((g) => g.displayId === targetGroupDisplayId)?.groupId;
  }, [phoneBooksEntity, targetGroupDisplayId]);

  // 配達フロー用: isPhoneBookEnabledを取得する
  const isPhoneBookEnabled = useMemo((): boolean => {
    const basic =
      dbData?.basics.find((b) => b.id === targetGroupDisplayId) ??
      dbData?.basics.find((b) => b.id === 0);
    return basic?.isPhoneBookEnabled ?? false;
  }, [dbData, targetGroupDisplayId]);

  // 派生値を2つのuseMemoに分割して複雑度を抑制
  const isDisplayAppointmentExists = menuButton?.isDisplayAppointmentExists ?? false;

  const { instructionText, instructionSubText } = useMemo(
    () => ({
      instructionText: menuWords?.menuHeaderTitle ?? "",
      instructionSubText: menuWords?.menuHeaderSubTitle ?? "",
    }),
    [menuWords]
  );

  const handleBack = useCallback(() => {
    if (phoneBooksEntity?.isSelectCompanyEnabled) {
      navigate(ROUTE.RECEPTION.COMPANY, { replace: true });
    } else {
      navigate(ROUTE.RECEPTION.LOADING, { replace: true, state: { skipStartupModal: true } });
    }
  }, [navigate, phoneBooksEntity]);

  // buttonOrderデータをもとに表示するボタン一覧を生成（表示フラグtrue、indexで昇順）
  const visibleButtons = useMemo(() => getVisibleButtons(menuButton), [menuButton]);

  // index.tsx と同じレイアウトルール: 上段(tall)・下段(wide)の分割数を算出
  const { topCount, bottomCount } = useMemo(
    () => getLayoutCounts(visibleButtons.length),
    [visibleButtons.length]
  );

  // deliveryTransitionType === 0: 配達受付を直接処理する
  const handleDirectDelivery = useCallback(async () => {
    if (!activationData || !menuButton) return;

    const plan = activationData.plan.toUpperCase();
    const notificationType = menuButton.deliveryNotificationType;
    const callType = menuButton.deliveryCallType || null;
    const extensionNumber = menuButton.deliveryExtensionNumber;

    setIsLoading(true);

    const receptionBody: Record<string, unknown> = {
      route: "DELIVERY",
      affiliation: undefined,
      visitorName: undefined,
      business: undefined,
      groupId: deliveryGroupId,
      groupName: undefined,
      groupUserId: undefined,
      userName: "",
      buttonName: deliveryButtonName,
      notificationType,
    };

    const onPhone = () => navigate(ROUTE.RECEPTION.CALLING, { state: { extensionNumber } });

    const flowType = resolveDeliveryFlowType(plan, notificationType);
    if (flowType === "call") {
      resolveCallDestinationAction(
        callType,
        extensionNumber,
        isPhoneBookEnabled,
        FACETIME_PHONEBOOK_CONFLICT,
        NO_CALL_DESTINATION,
        onPhone,
        navigate
      );
      setIsLoading(false);
      return;
    }
    if (flowType === "chat") {
      await executeDeliveryChatPost(
        receptionBody,
        () => navigate(ROUTE.RECEPTION.COMPLETE),
        (error) => {
          navigate(
            buildErrorPath(buildDeliveryReceptionErrorCode(error, ScreenType.MENU_RECEPTION))
          );
        }
      );
      setIsLoading(false);
      return;
    }
    if (flowType === "chatCall") {
      await postDeliveryReceptionIgnoreError(receptionBody);
      resolveCallDestinationAction(
        callType,
        extensionNumber,
        isPhoneBookEnabled,
        FACETIME_PHONEBOOK_CONFLICT,
        NO_CALL_DESTINATION,
        onPhone,
        navigate
      );
      setIsLoading(false);
      return;
    }
    if (flowType === "unknown") {
      setIsLoading(false);
      navigateToMenuReceptionError();
      return;
    }
  }, [
    activationData,
    deliveryButtonName,
    deliveryGroupId,
    isPhoneBookEnabled,
    menuButton,
    navigate,
    navigateToMenuReceptionError,
  ]);

  // 配達ボタン押下時のフロー分岐ハンドラ
  const handleDeliveryClick = useCallback(async () => {
    const deliveryTransitionType = menuButton?.deliveryTransitionType ?? 2;
    setReceptionButtonType(DELIVERY_BUTTON_TYPE);

    if (deliveryTransitionType === 1) {
      navigate(ROUTE.RECEPTION.DELIVERY_MAP);
      return;
    }

    if (deliveryTransitionType === 2) {
      navigate(ROUTE.RECEPTION.DELIVERY_COMPANIES_LIST);
      return;
    }

    await handleDirectDelivery();
  }, [handleDirectDelivery, menuButton, navigate, setReceptionButtonType]);

  // 共通: 一般受付 API を叩いて通知種別ごとの後処理を行うヘルパー
  async function postGeneralReceptionAndHandle(
    routeName: string,
    buttonTitle: string | undefined,
    notificationType: string,
    onCall: (chatRequestType: ChatRequestType) => void,
    navigateToComplete: () => void,
    navigateToError: (err: unknown) => void,
    navigateToErrorCode: (code: string) => void,
    navigateToInvalidChatStatus: () => void
  ) {
    const handlers: GeneralReceptionHandlers = {
      onCall,
      navigateToComplete,
      navigateToError,
      navigateToErrorCode,
      navigateToInvalidChatStatus,
    };

    setIsLoading(true);
    try {
      const response = await apiClient.post<{ data?: { isChatSuccess?: boolean } }>(
        buildIfaceApiUrl("v1/receptions/general"),
        {
          route: routeName,
          affiliation: undefined,
          visitorName: undefined,
          business: undefined,
          groupId: generalReceptionGroupId,
          buttonName: buttonTitle ?? "",
          notificationType,
        },
        { headers: createIfaceHeaders(true) }
      );
      handleGeneralReceptionSuccess(notificationType, response.data?.data?.isChatSuccess, handlers);
    } catch (error) {
      handleGeneralReceptionFailure(notificationType, error, handlers);
    } finally {
      setIsLoading(false);
    }
  }

  const handleInterviewClick = useCallback(async () => {
    if (!menuButton) {
      return;
    }
    setReceptionButtonType(RECRUIT_BUTTON_TYPE);
    const plan = (activationData?.plan ?? "").toUpperCase();
    const notificationType = menuButton.recruitNotificationType.toUpperCase();

    const navigateToVisitorInfo = () => {
      navigate(ROUTE.RECEPTION.VISITOR_INFO_INPUT);
    };

    const navigateToCalling = () => {
      navigate(
        {
          pathname: ROUTE.RECEPTION.CALLING,
        },
        { state: { chatRequestType: "noRequest" satisfies ChatRequestType } }
      );
    };

    const navigateToComplete = () => {
      navigate(ROUTE.RECEPTION.COMPLETE);
    };

    const navigateToError = (error: unknown) => {
      navigate(buildErrorPath(buildGeneralReceptionErrorCode(error)));
    };

    const navigateToErrorCode = (code: string) => {
      navigate(buildErrorPath(code));
    };

    const navigateToInvalidChatStatus = () => {
      navigate(buildErrorPath(INVALID_CHAT_STATUS));
    };

    if (menuButton.isDisplayVisitorInfoRecruit) {
      navigateToVisitorInfo();
      return;
    }

    if (isCallFlow(plan, notificationType)) {
      resolveCallDestinationAction(
        menuButton.recruitCallType || null,
        menuButton.recruitExtensionNumber,
        isPhoneBookEnabled,
        FACETIME_PHONEBOOK_CONFLICT,
        NO_CALL_DESTINATION,
        navigateToCalling,
        navigate
      );
      return;
    }

    if (!isChatReceptionFlow(plan, notificationType)) {
      navigateToMenuReceptionError();
      return;
    }

    await postGeneralReceptionAndHandle(
      "RECRUIT",
      menuWords?.recruitButtonTitle,
      notificationType,
      (chatRequestType) =>
        resolveCallDestinationAction(
          menuButton.recruitCallType || null,
          menuButton.recruitExtensionNumber,
          isPhoneBookEnabled,
          FACETIME_PHONEBOOK_CONFLICT,
          NO_CALL_DESTINATION,
          () => {
            navigate(
              {
                pathname: ROUTE.RECEPTION.CALLING,
              },
              { state: { chatRequestType } }
            );
          },
          navigate
        ),
      navigateToComplete,
      navigateToError,
      navigateToErrorCode,
      navigateToInvalidChatStatus
    );
  }, [
    activationData,
    isPhoneBookEnabled,
    menuButton,
    menuWords,
    navigate,
    navigateToMenuReceptionError,
    setReceptionButtonType,
  ]);

  const handleSecretaryClick = useCallback(async () => {
    if (!menuButton) {
      return;
    }
    setReceptionButtonType(SECRETARY_BUTTON_TYPE);

    if (menuButton.isDisplayVisitorInfoSecretary) {
      navigate(ROUTE.RECEPTION.VISITOR_INFO_INPUT);
      return;
    }

    const plan = (activationData?.plan ?? "").toUpperCase();
    const notificationType = menuButton.secretaryNotificationType.toUpperCase();

    if (isCallFlow(plan, notificationType)) {
      resolveCallDestinationAction(
        menuButton.secretaryCallType || null,
        menuButton.secretaryExtensionNumber,
        isPhoneBookEnabled,
        FACETIME_PHONEBOOK_CONFLICT,
        NO_CALL_DESTINATION,
        () => {
          navigate(
            {
              pathname: ROUTE.RECEPTION.CALLING,
            },
            { state: { chatRequestType: "noRequest" satisfies ChatRequestType } }
          );
        },
        navigate
      );
      return;
    }

    if (isChatReceptionFlow(plan, notificationType)) {
      const navigateToCompleteSec = () => {
        navigate(ROUTE.RECEPTION.COMPLETE);
      };

      const navigateToErrorSec = (err: unknown) => {
        navigate(buildErrorPath(buildGeneralReceptionErrorCode(err)));
      };

      const navigateToErrorCodeSec = (code: string) => {
        navigate(buildErrorPath(code));
      };

      const navigateToInvalidChatStatusSec = () => {
        navigate(buildErrorPath(INVALID_CHAT_STATUS));
      };

      await postGeneralReceptionAndHandle(
        "SECRETARY",
        menuWords?.secretaryButtonTitle,
        notificationType,
        (chatRequestType) =>
          resolveCallDestinationAction(
            menuButton.secretaryCallType || null,
            menuButton.secretaryExtensionNumber,
            isPhoneBookEnabled,
            FACETIME_PHONEBOOK_CONFLICT,
            NO_CALL_DESTINATION,
            () => {
              navigate(
                {
                  pathname: ROUTE.RECEPTION.CALLING,
                },
                { state: { chatRequestType } }
              );
            },
            navigate
          ),
        navigateToCompleteSec,
        navigateToErrorSec,
        navigateToErrorCodeSec,
        navigateToInvalidChatStatusSec
      );

      return;
    }

    navigateToMenuReceptionError();
  }, [
    activationData,
    isPhoneBookEnabled,
    menuButton,
    menuWords,
    navigate,
    navigateToMenuReceptionError,
    setReceptionButtonType,
  ]);

  const handleOtherClick = useCallback(async () => {
    if (!menuButton) {
      return;
    }
    setReceptionButtonType(OTHER_BUTTON_TYPE);

    if (menuButton.isDisplayVisitorInfoOther) {
      navigate(ROUTE.RECEPTION.VISITOR_INFO_INPUT);
      return;
    }

    const plan = (activationData?.plan ?? "").toUpperCase();
    const notificationType = menuButton.otherNotificationType.toUpperCase();

    const navigateToInputExtension = (chatRequestType: ChatRequestType) => {
      navigate(
        {
          pathname: ROUTE.RECEPTION.INPUT_EXTENSION_NUMBER,
        },
        { state: { chatRequestType } }
      );
    };

    const navigateToCalling = (chatRequestType: ChatRequestType) => {
      navigate(
        {
          pathname: ROUTE.RECEPTION.CALLING,
        },
        { state: { chatRequestType } }
      );
    };

    if (isCallFlow(plan, notificationType)) {
      if (menuButton.isDisplayInputExtensionNumber) {
        navigateToInputExtension("noRequest");
        return;
      }

      resolveCallDestinationAction(
        menuButton.otherCallType || null,
        menuButton.otherExtensionNumber,
        isPhoneBookEnabled,
        FACETIME_PHONEBOOK_CONFLICT,
        NO_CALL_DESTINATION,
        () => navigateToCalling("noRequest"),
        navigate
      );
      return;
    }

    if (isChatReceptionFlow(plan, notificationType)) {
      const navigateToCompleteOther = () => {
        navigate(ROUTE.RECEPTION.COMPLETE);
      };

      const navigateToErrorOther = (error: unknown) => {
        navigate(buildErrorPath(buildGeneralReceptionErrorCode(error)));
      };

      const navigateToErrorCodeOther = (code: string) => {
        navigate(buildErrorPath(code));
      };

      const navigateToInvalidChatStatusOther = () => {
        navigate(buildErrorPath(INVALID_CHAT_STATUS));
      };

      await postGeneralReceptionAndHandle(
        "OTHER",
        menuWords?.otherButtonTitle,
        notificationType,
        (chatRequestType) => {
          if (menuButton.isDisplayInputExtensionNumber) {
            navigateToInputExtension(chatRequestType);
            return;
          }

          resolveCallDestinationAction(
            menuButton.otherCallType || null,
            menuButton.otherExtensionNumber,
            isPhoneBookEnabled,
            FACETIME_PHONEBOOK_CONFLICT,
            NO_CALL_DESTINATION,
            () => navigateToCalling(chatRequestType),
            navigate
          );
        },
        navigateToCompleteOther,
        navigateToErrorOther,
        navigateToErrorCodeOther,
        navigateToInvalidChatStatusOther
      );

      return;
    }

    navigateToMenuReceptionError();
  }, [
    activationData,
    isPhoneBookEnabled,
    menuButton,
    menuWords,
    navigate,
    navigateToMenuReceptionError,
    setReceptionButtonType,
  ]);

  const handleTaxiClick = useCallback(async () => {
    if (!menuButton) {
      return;
    }
    setReceptionButtonType(TAXI_BUTTON_TYPE);
    const plan = (activationData?.plan ?? "").toUpperCase();
    const notificationType = menuButton.taxiNotificationType.toUpperCase();

    const navigateToCalling = () => {
      navigate(
        {
          pathname: ROUTE.RECEPTION.CALLING,
        },
        { state: { chatRequestType: "noRequest" satisfies ChatRequestType } }
      );
    };

    if (isCallFlow(plan, notificationType)) {
      resolveCallDestinationAction(
        menuButton.taxiCallType || null,
        menuButton.taxiExtensionNumber,
        isPhoneBookEnabled,
        FACETIME_PHONEBOOK_CONFLICT,
        NO_CALL_DESTINATION,
        navigateToCalling,
        navigate
      );
      return;
    }

    if (isChatReceptionFlow(plan, notificationType)) {
      const navigateToCompleteTaxi = () => {
        navigate(ROUTE.RECEPTION.COMPLETE);
      };

      const navigateToErrorTaxi = (err: unknown) => {
        navigate(buildErrorPath(buildGeneralReceptionErrorCode(err)));
      };

      const navigateToErrorCodeTaxi = (code: string) => {
        navigate(buildErrorPath(code));
      };

      const navigateToInvalidChatStatusTaxi = () => {
        navigate(buildErrorPath(INVALID_CHAT_STATUS));
      };

      await postGeneralReceptionAndHandle(
        "TAXI",
        menuWords?.taxiButtonTitle,
        notificationType,
        (chatRequestType) =>
          resolveCallDestinationAction(
            menuButton.taxiCallType || null,
            menuButton.taxiExtensionNumber,
            isPhoneBookEnabled,
            FACETIME_PHONEBOOK_CONFLICT,
            NO_CALL_DESTINATION,
            () => {
              navigate(
                {
                  pathname: ROUTE.RECEPTION.CALLING,
                },
                { state: { chatRequestType } }
              );
            },
            navigate
          ),
        navigateToCompleteTaxi,
        navigateToErrorTaxi,
        navigateToErrorCodeTaxi,
        navigateToInvalidChatStatusTaxi
      );

      return;
    }

    navigateToMenuReceptionError();
  }, [
    activationData,
    generalReceptionGroupId,
    isPhoneBookEnabled,
    menuButton,
    menuWords,
    navigate,
    navigateToMenuReceptionError,
    setReceptionButtonType,
  ]);

  // ボタン遷移先を決定する関数
  // eslint-disable-next-line complexity
  const handleButtonClick = (btn: ButtonConfig) => {
    const selectedButtonType = BUTTON_TYPE_BY_MENU_KEY[btn.menuWordsKey] ?? "";

    if (btn.menuWordsKey === "deliveryButtonTitle") {
      void handleDeliveryClick();
      return;
    }
    if (btn.menuWordsKey === "recruitButtonTitle") {
      void handleInterviewClick();
      return;
    }
    if (btn.menuWordsKey === "otherButtonTitle") {
      void handleOtherClick();
      return;
    }
    if (btn.menuWordsKey === "secretaryButtonTitle") {
      void handleSecretaryClick();
      return;
    }
    if (btn.menuWordsKey === "taxiButtonTitle") {
      void handleTaxiClick();
      return;
    }
    const redirect = VISITOR_INFO_REDIRECT_MAP[btn.menuWordsKey];
    if (redirect && menuButton?.[redirect.flagKey]) {
      setReceptionButtonType(redirect.buttonType);
      navigate(ROUTE.RECEPTION.VISITOR_INFO_INPUT);
    } else {
      if (btn.route) {
        setReceptionButtonType(selectedButtonType);
        navigate(btn.route);
      }
    }
  };

  useInactivityTimeout(navigate);

  useEffect(() => {
    void playWithCooldown(instructionText).catch((error) => {
      console.error("menuReception playVoice failed:", error);
    });
  }, [playWithCooldown]);

  return (
    <div className="menu-reception-page">
      <ReceptionHeader showBackButton onBackButtonClick={handleBack} />
      {isLoading && <ReceptionCommunicatingIndicator />}
      <div className="menu-reception-screen">
        {menuButton?.buttonOrder?.isDisplayQrButton && (
          <p className="reception_qr-trademark-note">{t("receptionQrTrademark")}</p>
        )}
        <div className="menu-reception-screen__content">
          <div className="screen__text">
            <h2 className="screen__title">{instructionText}</h2>
            {instructionSubText && (
              <p className="menu-reception-sub-instruction">{instructionSubText}</p>
            )}
          </div>
          {isDisplayAppointmentExists && (
            <div
              className="menu-reception-appointment"
              role="radiogroup"
              aria-labelledby="menu-reception-appointment-label"
            >
              <span
                id="menu-reception-appointment-label"
                style={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0, 0, 0, 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              >
                {t("menuReception.appointment.label", { defaultValue: "Appointment" })}
              </span>
              <label className="menu-reception-appointment-option">
                <input
                  type="radio"
                  name="appointment"
                  value="exists"
                  checked={appointmentExists}
                  onChange={() => setAppointmentExists(true)}
                />
                <span className="appointment-option-text typo-text2">
                  {t("menuReception.appointment.exists")}
                </span>
              </label>
              <label className="menu-reception-appointment-option">
                <input
                  type="radio"
                  name="appointment"
                  value="none"
                  checked={!appointmentExists}
                  onChange={() => setAppointmentExists(false)}
                />
                <span className="appointment-option-text typo-text2">
                  {t("menuReception.appointment.none")}
                </span>
              </label>
            </div>
          )}
          <section className="menu-reception-grid" aria-label="menu reception options">
            {visibleButtons.map((btn, index) => (
              <MenuButtonCard
                key={btn.menuWordsKey}
                btn={btn}
                isTop={index < topCount}
                topCount={topCount}
                bottomCount={bottomCount}
                label={menuWords?.[btn.menuWordsKey] ?? ""}
                onClick={() => handleButtonClick(btn)}
              />
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};

export default MenuReceptionPage;
